/**
 * AYU Wearables-Integration
 *
 * Zeigt verfügbare Wearable-Integrationen (Apple Watch, Fitbit, Garmin, Oura Ring)
 * mit "Bald verfügbar"-Status und Warteliste-Funktion.
 * Design: MENTO-Stil 2x2 Grid mit AYU-Branding.
 */
import { useState, useMemo } from "react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import {
  Watch,
  Activity,
  Zap,
  CircleDot,
  Bell,
  BellOff,
  Clock,
  Link2,
  RefreshCw,
  Sparkles,
  Shield,
  Moon,
  Heart,
  Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/* ─── Wearable-Daten ─── */
interface WearableInfo {
  id: "apple_watch" | "fitbit" | "garmin" | "oura_ring";
  name: string;
  expectedDate: string;
  description: string;
  features: string[];
  integrationTags: { label: string; icon: React.ReactNode }[];
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

const WEARABLES: WearableInfo[] = [
  {
    id: "apple_watch",
    name: "Apple Watch",
    expectedDate: "Q3 2026",
    description:
      "Automatische Schlaf-Erkennung, Herzfrequenz-Variabilität und Achtsamkeits-Erinnerungen direkt am Handgelenk.",
    features: [
      "Schlaf-Tracking automatisch",
      "HRV-Daten in Echtzeit",
      "Stress-Level Monitoring",
      "Achtsamkeits-Erinnerungen",
    ],
    integrationTags: [
      { label: "Schlaf", icon: <Moon className="w-3 h-3" /> },
      { label: "HRV", icon: <Heart className="w-3 h-3" /> },
      { label: "Stress", icon: <Brain className="w-3 h-3" /> },
    ],
    icon: <Watch className="w-6 h-6" />,
    iconBg: "bg-[#5a6b3c]/10",
    iconColor: "text-[#5a6b3c]",
  },
  {
    id: "fitbit",
    name: "Fitbit",
    expectedDate: "Q3 2026",
    description:
      "Detaillierte Schlafphasen-Analyse, Stress-Management-Score und tägliche Bereitschaftsbewertung.",
    features: [
      "Schlafphasen-Analyse",
      "Stress-Management-Score",
      "Täglicher Readiness-Score",
      "Aktivitäts-Tracking",
    ],
    integrationTags: [
      { label: "Schlaf", icon: <Moon className="w-3 h-3" /> },
      { label: "HRV", icon: <Heart className="w-3 h-3" /> },
      { label: "Stress", icon: <Brain className="w-3 h-3" /> },
    ],
    icon: <Activity className="w-6 h-6" />,
    iconBg: "bg-[#7c8b6e]/10",
    iconColor: "text-[#7c8b6e]",
  },
  {
    id: "garmin",
    name: "Garmin",
    expectedDate: "Q4 2026",
    description:
      "Body Battery, Schlaf-Score und Stress-Tracking für eine ganzheitliche Gesundheitsübersicht.",
    features: [
      "Body Battery Energie-Level",
      "Schlaf-Score & Phasen",
      "Ganztägiges Stress-Tracking",
      "Puls-Ox Messung",
    ],
    integrationTags: [
      { label: "Schlaf", icon: <Moon className="w-3 h-3" /> },
      { label: "HRV", icon: <Heart className="w-3 h-3" /> },
      { label: "Stress", icon: <Brain className="w-3 h-3" /> },
    ],
    icon: <Zap className="w-6 h-6" />,
    iconBg: "bg-[#4a7c5c]/10",
    iconColor: "text-[#4a7c5c]",
  },
  {
    id: "oura_ring",
    name: "Oura Ring",
    expectedDate: "Q4 2026",
    description:
      "Der diskreteste Gesundheitstracker — perfekte Schlafanalyse, Temperatur-Trends und Readiness-Score.",
    features: [
      "Präzise Schlafanalyse",
      "Körpertemperatur-Trends",
      "Readiness-Score",
      "Zyklus-Tracking",
    ],
    integrationTags: [
      { label: "Schlaf", icon: <Moon className="w-3 h-3" /> },
      { label: "HRV", icon: <Heart className="w-3 h-3" /> },
      { label: "Stress", icon: <Brain className="w-3 h-3" /> },
    ],
    icon: <CircleDot className="w-6 h-6" />,
    iconBg: "bg-[#8b6914]/10",
    iconColor: "text-[#8b6914]",
  },
];

/* ─── Wearable Card Component ─── */
function WearableCard({
  wearable,
  isOnWaitlist,
  onToggleWaitlist,
  isLoading,
}: {
  wearable: WearableInfo;
  isOnWaitlist: boolean;
  onToggleWaitlist: () => void;
  isLoading: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="bg-white rounded-2xl border border-[#e8e0d4] overflow-hidden hover:shadow-md transition-shadow duration-300">
      {/* Header */}
      <div className="p-5 pb-3">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-12 h-12 rounded-xl ${wearable.iconBg} flex items-center justify-center ${wearable.iconColor}`}>
            {wearable.icon}
          </div>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#7c8b6e]/15 text-[#5a6b4a] border border-[#7c8b6e]/30">
            {t("wearables.baldVerfuegbar")}
          </span>
        </div>

        <h3 className="text-lg font-semibold text-[#2d3a1a] mb-0.5">{wearable.name}</h3>
        <p className="text-xs text-[#6b7f4e] font-medium tracking-wide uppercase">
          {t("wearables.baldVerfuegbar")}
        </p>
      </div>

      {/* Description */}
      <div className="px-5 pb-3">
        <p className="text-sm text-[#5a6b4a] leading-relaxed">{wearable.description}</p>
      </div>

      {/* Features */}
      <div className="px-5 pb-3">
        <ul className="space-y-1.5">
          {wearable.features.map((feature, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#5a6b4a]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5a6b3c]/40 shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {/* Integration Tags */}
      <div className="mx-5 mb-4 p-3 rounded-xl bg-[#f5f0e8]/60 border border-[#e8e0d4]/50">
        <p className="text-[10px] font-semibold text-[#8a9680] tracking-wider uppercase mb-2">
          {t("wearables.ayuIntegration")}
        </p>
        <div className="flex items-center gap-3">
          {wearable.integrationTags.map((tag, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs text-[#5a6b4a]">
              <span className="text-[#5a6b3c]/60">{tag.icon}</span>
              {tag.label}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-[#8a9680] mt-1.5">
          {t("wearables.datenFliessen")}
        </p>
      </div>

      {/* CTA Button */}
      <div className="px-5 pb-5">
        <Button
          onClick={onToggleWaitlist}
          disabled={isLoading}
          variant={isOnWaitlist ? "outline" : "default"}
          className={`w-full rounded-xl h-11 text-sm font-medium transition-all ${
            isOnWaitlist
              ? "border-[#d4dcc4] text-[#5a6b4a] hover:bg-[#f5f0e8] bg-[#f5f0e8]/50"
              : "bg-[#5a6b3c] hover:bg-[#4a5b2c] text-white"
          }`}
        >
          {isLoading ? (
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          ) : isOnWaitlist ? (
            <Clock className="w-4 h-4 mr-2" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          {isOnWaitlist ? t("wearables.warteliste") : t("wearables.benachrichtige")}
        </Button>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function AppWearables() {
  const { t } = useI18n();
  const { client } = useClientAuth();
  const [loadingWearable, setLoadingWearable] = useState<string | null>(null);

  const { data: myWaitlist = [], refetch } = trpc.wearables.getMyWaitlist.useQuery(
    { clientId: client?.clientId || "" },
    { enabled: !!client?.clientId }
  );

  const joinMutation = trpc.wearables.joinWaitlist.useMutation({
    onSuccess: (data) => {
      if (data.alreadyOnList) {
        toast.info(t("wearables.bereitsWarteliste"));
      } else {
        toast.success(t("wearables.benachrichtigtBald"));
      }
      refetch();
      setLoadingWearable(null);
    },
    onError: () => {
      toast.error(t("wearables.fehlerEintragen"));
      setLoadingWearable(null);
    },
  });

  const leaveMutation = trpc.wearables.leaveWaitlist.useMutation({
    onSuccess: () => {
      toast.info(t("wearables.entfernt"));
      refetch();
      setLoadingWearable(null);
    },
    onError: () => {
      toast.error(t("wearables.fehlerAustragen"));
      setLoadingWearable(null);
    },
  });

  const waitlistCount = myWaitlist.length;

  const handleToggleWaitlist = (wearableType: WearableInfo["id"]) => {
    if (!client?.clientId) return;
    setLoadingWearable(wearableType);
    const isOnList = myWaitlist.includes(wearableType);
    if (isOnList) {
      leaveMutation.mutate({ clientId: client.clientId, wearableType });
    } else {
      joinMutation.mutate({ clientId: client.clientId, wearableType });
    }
  };

  return (
    <div className="h-full overflow-y-auto px-4 pt-4 pb-8 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#2d3a1a]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t("wearables.title")}
          </h1>
          <p className="text-sm text-[#6b7c5a] mt-1">
            {t("wearables.subtitle")}
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-[#f5f0e8] rounded-2xl border border-[#e8e0d4] p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#5a6b3c]/10 flex items-center justify-center shrink-0">
              <Watch className="w-5 h-5 text-[#5a6b3c]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#2d3a1a] mb-1">
                {t("wearables.integrationen")}
              </h2>
              <p className="text-sm text-[#5a6b4a] leading-relaxed">
                {t("wearables.integrationenDesc")}
              </p>
              {waitlistCount > 0 && (
                <p className="text-sm font-medium text-[#5a6b3c] mt-2">
                  {waitlistCount > 1
                    ? t("wearables.wartelistenN", { n: String(waitlistCount) })
                    : t("wearables.wartelisteN", { n: String(waitlistCount) })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Wearable Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {WEARABLES.map((wearable) => (
            <WearableCard
              key={wearable.id}
              wearable={wearable}
              isOnWaitlist={myWaitlist.includes(wearable.id)}
              onToggleWaitlist={() => handleToggleWaitlist(wearable.id)}
              isLoading={loadingWearable === wearable.id}
            />
          ))}
        </div>

        {/* How it works */}
        <div className="bg-white rounded-2xl border border-[#e8e0d4] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#2d3a1a] mb-5">{t("wearables.soFunktioniert")}</h2>
          <div className="space-y-5">
            {[
              {
                num: "1",
                title: t("wearables.step1Title"),
                desc: t("wearables.step1Desc"),
                icon: <Link2 className="w-4 h-4" />,
              },
              {
                num: "2",
                title: t("wearables.step2Title"),
                desc: t("wearables.step2Desc"),
                icon: <RefreshCw className="w-4 h-4" />,
              },
              {
                num: "3",
                title: t("wearables.step3Title"),
                desc: t("wearables.step3Desc"),
                icon: <Sparkles className="w-4 h-4" />,
              },
            ].map((step) => (
              <div key={step.num} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#5a6b3c]/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-[#5a6b3c]">{step.num}</span>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#2d3a1a] mb-0.5">{step.title}</h3>
                  <p className="text-sm text-[#6b7c5a]">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy Note */}
        <div className="text-center pb-4">
          <div className="inline-flex items-center gap-2 text-xs text-[#8a9680]">
            <Shield className="w-3.5 h-3.5" />
            <span>
              {t("wearables.datenschutz")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
